import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  CreditCard, 
  CheckCircle2,
  Clock,
  DollarSign,
  ArrowRight,
  Package
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: 49,
      description: "Perfect for small teams",
      features: ["Up to 10 employees", "All safety courses", "Basic analytics", "Email support"],
      popular: false
    },
    {
      id: "professional",
      name: "Professional",
      price: 149,
      description: "For growing organizations",
      features: ["Up to 50 employees", "All safety courses", "Advanced analytics", "Custom branding", "Priority support"],
      popular: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: 399,
      description: "For large enterprises",
      features: ["Up to 500 employees", "All safety courses", "Custom courses", "API access", "Dedicated support", "SSO integration"],
      popular: false
    }
  ];

  useEffect(() => {
    fetchTransactions();
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      try {
        const response = await axios.get(`${API}/billing/status/${sessionId}`, { withCredentials: true });
        if (response.data.payment_status === 'paid') {
          toast.success("Payment successful! Your subscription has been activated.");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
      }
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/billing/transactions`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await axios.post(
        `${API}/billing/checkout?plan=${planId}`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to create checkout session");
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="billing-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Billing & Subscriptions
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your subscription plan and view payment history.
          </p>
        </div>

        {/* Pricing Plans */}
        <div>
          <h2 className="font-heading text-xl font-semibold text-primary mb-6">Choose Your Plan</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  plan.popular 
                    ? 'border-2 border-accent shadow-lg' 
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
                data-testid={`billing-plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="font-heading text-xl font-semibold text-primary">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  
                  <div className="mb-6">
                    <span className="font-heading text-4xl font-bold text-primary">${plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={processingPlan === plan.id}
                    className={`w-full ${plan.popular ? 'bg-accent hover:bg-accent/90' : 'bg-primary hover:bg-primary/90'}`}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Subscribe
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn) => (
                      <tr key={txn.transaction_id} data-testid={`transaction-${txn.transaction_id}`}>
                        <td>
                          {new Date(txn.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium capitalize">{txn.plan}</span>
                          </div>
                        </td>
                        <td>
                          <span className="font-medium">${txn.amount.toFixed(2)}</span>
                          <span className="text-muted-foreground text-sm ml-1">{txn.currency.toUpperCase()}</span>
                        </td>
                        <td>
                          <Badge className={`badge-${txn.payment_status === 'paid' ? 'success' : txn.payment_status === 'pending' ? 'warning' : 'danger'}`}>
                            {txn.payment_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No payment history yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
